"use strict";

/**
 * Publishes an event to dataLayer.
 * @param {string} event - Event name.
 * @param {Object} data - Event data.
 */
function pushToDataLayer(event, data) {
  window.dataLayer.push({
    event: event,
    ecommerce: data,
  });
}

/**
 * Extracts relevant product data for dl events.
 * @param {Object} product - Product object.
 * @returns {Object} Simplified product data.
 */
function formatProductData(product) {
  const {
    item_name,
    item_id,
    price,
    discount,
    item_category,
    item_variant,
    item_list_name,
    item_list_id,
    index,
    quantity,
    shipping_price,
  } = product;

  return {
    item_name,
    item_id,
    price,
    discount,
    item_category,
    item_variant,
    item_list_name,
    item_list_id,
    index,
    quantity,
    shipping_price,
  };
}

/**
 * Tracks an event by sending it to dataLayer.
 * @param {string} event - Event name.
 * @param {Object} data - Event data.
 */
function trackEvent(event, data) {
  // console.log("trackEvent:", event, data);
  pushToDataLayer(event, data);
}

/**
 * Handles click events for event tracking.
 * @param {MouseEvent} event - Click event.
 */
function handleClickEvent(event) {
  try {
    const target = event.target?.closest?.("[data-dl-onclick]");
    if (!target) return;

    const { dlOnclick: eventName, index = 0, dlId: sectionId } = target.dataset;
    const eventData = window?.dlPreparedData?.[sectionId] || {};

    if (eventName && eventData.items?.[index]) {
      trackEvent(eventName, { items: [eventData.items[index]] });
    }
  } catch (error) {
    console.error("Error handling click event:", error);
  }
}

/**
 * Attaches IntersectionObserver to the node (and its descendants).
 * @param {Node} node - The node to check and observe.
 * @param {IntersectionObserver} io - Our IntersectionObserver instance.
 */
function attachIntersectionObserver(node, io) {
  if (node.nodeType === Node.ELEMENT_NODE && node.hasAttribute?.("data-dl-onview")) {
    io.observe(node);
  }
  node.querySelectorAll?.("[data-dl-onview]").forEach((el) => {
    io.observe(el);
  });
}

/**
 * Initializes IntersectionObserver for elements with data-dl-onview.
 * @returns {IntersectionObserver} Returns the instance for further use.
 */
function initializeViewTracking() {
  const io = new IntersectionObserver((entries, observer) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const element = entry.target;
        const { dlOnview: eventName, dlId: sectionId } = element.dataset;
        const jsonData = document.querySelector(`[data-dl-json-for-section=${sectionId}]`)?.textContent;
        const eventData = jsonData ? JSON.parse(jsonData) : window?.dlPreparedData?.[sectionId] || {};

        if (eventName) {
          trackEvent(eventName, eventData);
          observer.unobserve(element);
        }
      }
    });
  });

  // Observe all initial elements with data-dl-onview
  document.querySelectorAll("[data-dl-onview]").forEach((el) => io.observe(el));

  return io;
}

/**
 * Initializes all needed listeners.
 */
function initializeTracking() {
  // Handle click events
  document.body.addEventListener("click", handleClickEvent);

  // Create an IntersectionObserver and attach to current elements
  const io = initializeViewTracking();

  // Create a MutationObserver to automatically detect new elements in DOM
  const mutationObserver = new MutationObserver((mutations) => {
    mutations.forEach(({ addedNodes }) => {
      addedNodes.forEach((node) => {
        attachIntersectionObserver(node, io);
      });
    });
  });

  mutationObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });
}

// Run everything after DOM is loaded
document.addEventListener("DOMContentLoaded", () => {
  const cartDrawer = document.querySelector("cart-drawer");
  if (cartDrawer) {
    cartDrawer.addEventListener("dialog:before-show", () => {
      trackEvent("view_cart", window?.dlPreparedData?.[cartDrawer.dataset?.dlId ?? ''] ?? 'data error, check theme.js');
    });
  }

  document.documentElement.addEventListener('variant:add', (event) => {
    trackEvent("add_to_cart", { items: [...event.detail.items] });
  })
  initializeTracking();
});